import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Star } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { friendlyError } from '@/lib/errors';
import { eventBusHub } from '@/lib/eventBusHub';
import { navigateTo } from '@/lib/navigate';
import { t } from '@/lib/site/i18n';
import { isSiteAuthenticated } from '@/lib/site/visibility';
import { registerWidget, type WidgetContext, type WidgetProps } from './registry';
import { AppearanceWrap, siteBlockText, type BlockAppearance } from './blocks';
import { registerSiteContactBlocks } from './blocks-contacts';

// ============================================================================
// lib/widgets/blocks-marketplace.tsx — ИНТЕРАКТИВ МАРКЕТПЛЕЙСА (Этап 3):
// form (гостевая заявка + honeypot), reviews/review_form/rating (отзывы),
// search/facets (поиск по сайту, состояние в URL). Регистрируются в
// registerSiteBlocks (builtin.tsx). Данные — публичные RPC (SSoT — сервер).
// ============================================================================

// ----------------------------- общее -----------------------------

/** ReviewTarget — объект отзывов (props или запись страницы объекта). */
function reviewTarget(props: WidgetProps, ctx: WidgetContext): { objectType: string; objectCode: string; objectId: string } {
  return {
    objectType: String(props.objectType || 'catalog'),
    objectCode: String(props.objectCode || ''),
    objectId: String(props.objectId || '') || String(ctx.record?.id || ''),
  };
}

function RatingStars({ value, size = 16 }: { value: number; size?: number }) {
  const full = Math.round(Math.max(0, Math.min(5, value)));
  return (
    <span className="inline-flex items-center gap-0.5" title={`${value.toFixed(1)} из 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} style={{ width: size, height: size }}
          className={i <= full ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/40'} />
      ))}
    </span>
  );
}

function ConfigHint({ text }: { text: string }) {
  return <div className="mx-4 my-2 rounded-lg border border-dashed p-3 text-xs text-muted-foreground">{text}</div>;
}

// ----------------------------- form (гостевая заявка) -----------------------------

interface FormFieldDef {
  name: string;
  label?: string;
  type?: 'text' | 'phone' | 'email' | 'textarea' | 'select' | 'date';
  required?: boolean;
  placeholder?: string;
  options?: string[];
}

interface FormSubmitDef {
  objectType?: string;
  objectName?: string;
  mapping?: Record<string, string>; // колонка заявки → имя поля формы
  thanks?: { title?: string; text?: string; redirect?: string };
}

// Прямые колонки CreateOrder (остальные mapping-колонки → extraFields).
const ORDER_DIRECT_COLUMNS = new Set([
  'name', 'phone', 'description', 'desired_date', 'desired_time', 'address_text', 'priority', 'source',
]);

function parseFormFields(raw: unknown, locale: string, defaultLocale: string, record?: Record<string, string>): FormFieldDef[] {
  const text = t(raw as never, locale, defaultLocale, record);
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((f) => f && typeof f.name === 'string' && f.name.trim()) as FormFieldDef[];
  } catch {
    return [];
  }
}

function parseFormSubmit(raw: unknown, locale: string, defaultLocale: string, record?: Record<string, string>): FormSubmitDef {
  const text = t(raw as never, locale, defaultLocale, record);
  if (!text) return {};
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' ? (parsed as FormSubmitDef) : {};
  } catch {
    return {};
  }
}

function FormBlock({ props, ctx }: { props: WidgetProps; ctx: WidgetContext }) {
  const title = siteBlockText(props, ctx, 'title');
  const submitLabel = siteBlockText(props, ctx, 'submitLabel') || 'Отправить';
  const fields = useMemo(
    () => parseFormFields(props.fields, ctx.siteLocale || '', ctx.siteDefaultLocale || '', ctx.record),
    [props.fields, ctx.siteLocale, ctx.siteDefaultLocale, ctx.record],
  );
  const submitDef = useMemo(
    () => parseFormSubmit(props.submit, ctx.siteLocale || '', ctx.siteDefaultLocale || '', ctx.record),
    [props.submit, ctx.siteLocale, ctx.siteDefaultLocale, ctx.record],
  );
  const thanks = submitDef.thanks || {};
  const [values, setValues] = useState<Record<string, string>>({});
  const [honeypot, setHoneypot] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Honeypot (антиспам MVP): скрытое поле заполнено ботом → тихий отказ.
    if (honeypot.trim() !== '') { setDone(true); return; }
    for (const f of fields) {
      if (f.required && !String(values[f.name] || '').trim()) {
        setError(`Заполните поле «${f.label || f.name}»`);
        return;
      }
    }
    // Маппинг submit.mapping {колонка: поле}; без маппинга — имя поля = колонка.
    const mapping = submitDef.mapping && Object.keys(submitDef.mapping).length > 0
      ? submitDef.mapping
      : Object.fromEntries(fields.map((f) => [f.name, f.name]));
    const direct: Record<string, string> = {};
    const extra: Record<string, string> = {};
    for (const [column, propName] of Object.entries(mapping)) {
      const v = String(values[propName] || '').trim();
      if (!v) continue;
      if (ORDER_DIRECT_COLUMNS.has(column)) direct[column] = v;
      else extra[column] = v;
    }
    setBusy(true);
    try {
      const sdk = getSDK();
      await sdk.order.createOrder({
        name: direct.name || '',
        phone: direct.phone || '',
        description: direct.description || '',
        desiredDate: direct.desired_date || '',
        desiredTime: direct.desired_time || '',
        addressText: direct.address_text || '',
        priority: direct.priority || '',
        source: direct.source || '',
        extraFields: extra,
      } as never);
      setDone(true);
      if (thanks.redirect) {
        if (/^https?:\/\//i.test(thanks.redirect)) window.location.href = thanks.redirect;
        else navigateTo(thanks.redirect);
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  if (fields.length === 0) return <ConfigHint text="Форма: добавьте поля (JSON [{name,label,type,required}]) в свойствах блока" />;

  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      {done ? (
        <div className="rounded-lg border border-[var(--site-border)] p-6 text-center text-[var(--site-text)]">
          {thanks.title && <div className="text-lg font-semibold mb-1">{thanks.title}</div>}
          <div>{thanks.text || 'Спасибо! Заявка отправлена.'}</div>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-3 max-w-xl">
          {title && <div className="text-xl font-semibold text-[var(--site-text)]">{title}</div>}
          {fields.map((f) => (
            <label key={f.name} className="flex flex-col gap-1 text-sm text-[var(--site-text)]">
              <span>{f.label || f.name}{f.required ? ' *' : ''}</span>
              {f.type === 'textarea' ? (
                <textarea className="min-h-[80px] rounded-lg border border-[var(--site-border)] bg-[var(--site-bg,#fff)] px-3 py-2 text-sm"
                  placeholder={f.placeholder} value={values[f.name] || ''} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} />
              ) : f.type === 'select' ? (
                <select className="h-9 rounded-lg border border-[var(--site-border)] bg-[var(--site-bg,#fff)] px-3 text-sm"
                  value={values[f.name] || ''} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}>
                  <option value="">— выберите —</option>
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={f.type === 'phone' ? 'tel' : f.type === 'email' ? 'email' : f.type === 'date' ? 'date' : 'text'}
                  className="h-9 rounded-lg border border-[var(--site-border)] bg-[var(--site-bg,#fff)] px-3 text-sm"
                  placeholder={f.placeholder} value={values[f.name] || ''} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} />
              )}
            </label>
          ))}
          {/* Honeypot: скрытое поле — заполняется только ботом */}
          <input type="text" name="_hp" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} aria-hidden="true" />
          {error && <div className="text-sm text-destructive">{error}</div>}
          <button type="submit" disabled={busy}
            className="inline-flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium bg-[var(--site-primary,var(--color-primary))] text-[var(--site-primary-foreground,#fff)] disabled:opacity-60">
            {busy ? 'Отправка…' : submitLabel}
          </button>
        </form>
      )}
    </AppearanceWrap>
  );
}

// ----------------------------- reviews -----------------------------

interface ReviewRow {
  id: string; authorName?: string; guestName?: string; rating: number; text: string; createdAt?: string;
}

function reviewAuthor(r: ReviewRow): string {
  return r.authorName || r.guestName || 'Гость';
}

function ReviewsBlock({ props, ctx }: { props: WidgetProps; ctx: WidgetContext }) {
  const target = reviewTarget(props, ctx);
  const limit = Math.max(1, Number(props.limit) || 10);
  const title = siteBlockText(props, ctx, 'title') || 'Отзывы';
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [avg, setAvg] = useState(0);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  // Live-обновление: событие data.review.* по этому объекту → перечитать.
  useEffect(() => {
    if (!target.objectCode || !target.objectId) return;
    return eventBusHub.subscribe((ev) => {
      if (!ev.topic.startsWith('data.review.')) return;
      const p = (ev.payload || {}) as Record<string, unknown>;
      if (String(p.object_id || '') === target.objectId) setReloadTick((n) => n + 1);
    });
  }, [target.objectCode, target.objectId]);

  useEffect(() => {
    if (!target.objectCode || !target.objectId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getSDK().review.listReviews({ ...target, page: 0, pageSize: limit });
        if (cancelled) return;
        setRows((resp.items || []) as ReviewRow[]);
        setTotal(resp.total || 0);
        setAvg(resp.ratingAvg || 0);
        setCount(resp.count || 0);
        setPage(0);
      } catch (e) {
        if (!cancelled) setError(friendlyError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [target.objectType, target.objectCode, target.objectId, limit, reloadTick]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const resp = await getSDK().review.listReviews({ ...target, page: next, pageSize: limit });
      setRows((prev) => [...prev, ...((resp.items || []) as ReviewRow[])]);
      setTotal(resp.total || 0);
      setPage(next);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoadingMore(false);
    }
  };

  if (!target.objectCode) return <ConfigHint text="Отзывы: укажите объект (тип и код) или откройте блок на странице объекта" />;

  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      <div className="flex items-center gap-3">
        <div className="text-xl font-semibold text-[var(--site-text)]">{title}</div>
        {count > 0 && (
          <span className="inline-flex items-center gap-1.5 text-sm text-[var(--site-text)] opacity-80">
            <RatingStars value={avg} /> {avg.toFixed(1)} · {count}
          </span>
        )}
      </div>
      {error && <div className="mt-2 text-sm text-destructive">{error}</div>}
      <div className="mt-3 flex flex-col gap-3">
        {rows.length === 0 && !error && <div className="text-sm text-muted-foreground">Пока нет отзывов</div>}
        {rows.map((r) => (
          <div key={r.id} className="rounded-lg border border-[var(--site-border)] p-3">
            <div className="flex items-center gap-2 text-sm text-[var(--site-text)]">
              <span className="font-medium">{reviewAuthor(r)}</span>
              <RatingStars value={r.rating} size={14} />
              {r.createdAt && <span className="text-xs opacity-60">{r.createdAt.slice(0, 10)}</span>}
            </div>
            <div className="mt-1 text-sm text-[var(--site-text)] whitespace-pre-wrap">{r.text}</div>
          </div>
        ))}
        {total > rows.length && (
          <button type="button" disabled={loadingMore} onClick={() => void loadMore()}
            className="self-start rounded-lg border border-[var(--site-border)] px-4 py-1.5 text-sm text-[var(--site-text)] disabled:opacity-60">
            {loadingMore ? 'Загрузка…' : `Показать ещё (${total - rows.length})`}
          </button>
        )}
      </div>
    </AppearanceWrap>
  );
}

// ----------------------------- review_form -----------------------------

function ReviewFormBlock({ props, ctx }: { props: WidgetProps; ctx: WidgetContext }) {
  const target = reviewTarget(props, ctx);
  const title = siteBlockText(props, ctx, 'title') || 'Оставить отзыв';
  const successText = siteBlockText(props, ctx, 'successText') || 'Спасибо за отзыв!';
  const authed = isSiteAuthenticated();
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [guestName, setGuestName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!text.trim()) { setError('Напишите текст отзыва'); return; }
    if (!authed && !guestName.trim()) { setError('Укажите имя'); return; }
    setBusy(true);
    try {
      await getSDK().review.createReview({ ...target, rating, text, guestName });
      setDone(true);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!target.objectCode || !target.objectId) return <ConfigHint text="Форма отзыва: укажите объект (тип/код) или откройте на странице объекта" />;

  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      {done ? (
        <div className="rounded-lg border border-[var(--site-border)] p-6 text-center text-[var(--site-text)]">{successText}</div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-3 max-w-xl">
          <div className="text-xl font-semibold text-[var(--site-text)]">{title}</div>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <button key={i} type="button" onClick={() => setRating(i)} title={`${i}`}>
                <Star style={{ width: 22, height: 22 }} className={i <= rating ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/40'} />
              </button>
            ))}
          </div>
          {!authed && (
            <label className="flex flex-col gap-1 text-sm text-[var(--site-text)]">
              <span>Имя *</span>
              <input className="h-9 rounded-lg border border-[var(--site-border)] bg-[var(--site-bg,#fff)] px-3 text-sm"
                value={guestName} onChange={(e) => setGuestName(e.target.value)} />
            </label>
          )}
          <label className="flex flex-col gap-1 text-sm text-[var(--site-text)]">
            <span>Отзыв *</span>
            <textarea className="min-h-[90px] rounded-lg border border-[var(--site-border)] bg-[var(--site-bg,#fff)] px-3 py-2 text-sm"
              value={text} onChange={(e) => setText(e.target.value)} />
          </label>
          {error && <div className="text-sm text-destructive">{error}</div>}
          <button type="submit" disabled={busy}
            className="inline-flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium bg-[var(--site-primary,var(--color-primary))] text-[var(--site-primary-foreground,#fff)] disabled:opacity-60">
            {busy ? 'Отправка…' : 'Отправить'}
          </button>
        </form>
      )}
    </AppearanceWrap>
  );
}

// ----------------------------- rating -----------------------------

function RatingBlock({ props, ctx }: { props: WidgetProps; ctx: WidgetContext }) {
  const target = reviewTarget(props, ctx);
  const [avg, setAvg] = useState(0);
  const [count, setCount] = useState(0);
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    if (!target.objectCode || !target.objectId) return;
    return eventBusHub.subscribe((ev) => {
      if (!ev.topic.startsWith('data.review.')) return;
      const p = (ev.payload || {}) as Record<string, unknown>;
      if (String(p.object_id || '') === target.objectId) setReloadTick((n) => n + 1);
    });
  }, [target.objectCode, target.objectId]);
  useEffect(() => {
    if (!target.objectCode || !target.objectId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getSDK().review.getReviewSummary({ objectType: target.objectType, objectCode: target.objectCode, objectIds: [target.objectId] });
        const item = (resp.items || [])[0];
        if (!cancelled && item) { setAvg(item.ratingAvg || 0); setCount(item.count || 0); }
      } catch { /* сводка не критична: блок покажет 0 */ }
    })();
    return () => { cancelled = true; };
  }, [target.objectType, target.objectCode, target.objectId, reloadTick]);
  if (!target.objectCode || !target.objectId) return <ConfigHint text="Рейтинг: укажите объект (тип/код) или откройте на странице объекта" />;
  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      <span className="inline-flex items-center gap-2 text-[var(--site-text)]">
        <RatingStars value={avg} /> <span className="font-medium">{avg.toFixed(1)}</span>
        <span className="text-sm opacity-70">({count})</span>
      </span>
    </AppearanceWrap>
  );
}

// ----------------------------- search / facets -----------------------------

interface SearchItem { title: string; url: string; excerpt: string; objectType: string; objectCode: string }
interface SearchFacetValue { value: string; label: string; count: number }
interface SearchFacet { field: string; values: SearchFacetValue[] }

function useSiteSearchQuery() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const facet = params.get('facet') || '';
  const setQuery = useCallback((next: string) => {
    const p = new URLSearchParams(params);
    if (next) p.set('q', next); else p.delete('q');
    p.delete('facet'); // новый запрос — фасет сбрасывается
    setParams(p, { replace: true });
  }, [params, setParams]);
  const setFacet = useCallback((next: string) => {
    const p = new URLSearchParams(params);
    if (next) p.set('facet', next); else p.delete('facet');
    setParams(p, { replace: true });
  }, [params, setParams]);
  return { q, facet, setQuery, setFacet };
}

function parseFacet(facet: string): { field: string; value: string } {
  const idx = facet.indexOf(':');
  if (idx < 0) return { field: '', value: '' };
  return { field: facet.slice(0, idx), value: facet.slice(idx + 1) };
}

function SearchBlock({ props, ctx }: { props: WidgetProps; ctx: WidgetContext }) {
  const { q, facet, setQuery } = useSiteSearchQuery();
  const [input, setInput] = useState(q);
  const [items, setItems] = useState<SearchItem[]>([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pageSize = Math.max(1, Number(props.pageSize) || 10);
  const facetFields = String(props.facets || '').split(',').map((s) => s.trim()).filter(Boolean);
  const f = parseFacet(facet);

  useEffect(() => { setInput(q); }, [q]);
  useEffect(() => {
    if (!q.trim() || !ctx.siteCode) { setItems([]); setTotal(0); return; }
    let cancelled = false;
    setBusy(true);
    setError('');
    (async () => {
      try {
        const resp = await getSDK().site.siteSearch({
          site: ctx.siteCode || '', q, page: 0, pageSize, facets: facetFields,
          filterField: f.field, filterValue: f.value,
        });
        if (cancelled) return;
        setItems((resp.items || []) as SearchItem[]);
        setTotal(resp.total || 0);
      } catch (e) {
        if (!cancelled) setError(friendlyError(e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, ctx.siteCode, pageSize, facetFields.join(','), f.field, f.value]);

  const open = (url: string) => {
    if (/^https?:\/\//i.test(url)) { window.open(url, '_blank', 'noopener'); return; }
    navigateTo(url);
  };

  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      <form className="flex gap-2 max-w-2xl" onSubmit={(e) => { e.preventDefault(); setQuery(input.trim()); }}>
        <input className="flex-1 h-10 rounded-lg border border-[var(--site-border)] bg-[var(--site-bg,#fff)] px-3 text-sm text-[var(--site-text)]"
          placeholder={siteBlockText(props, ctx, 'placeholder') || 'Поиск…'} value={input} onChange={(e) => setInput(e.target.value)} />
        <button type="submit" disabled={busy}
          className="rounded-lg px-5 text-sm font-medium bg-[var(--site-primary,var(--color-primary))] text-[var(--site-primary-foreground,#fff)] disabled:opacity-60">
          {busy ? '…' : 'Найти'}
        </button>
      </form>
      {error && <div className="mt-2 text-sm text-destructive">{error}</div>}
      {q && !busy && (
        <div className="mt-3 flex flex-col gap-2 max-w-2xl">
          {items.length === 0 && !error && <div className="text-sm text-muted-foreground">Ничего не найдено</div>}
          {items.map((it, i) => (
            <button key={`${it.objectType}.${it.objectCode}.${i}`} type="button" onClick={() => open(it.url)}
              className="text-left rounded-lg border border-[var(--site-border)] p-3 hover:opacity-80">
              <div className="font-medium text-[var(--site-text)]">{it.title}</div>
              {it.excerpt && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{it.excerpt}</div>}
            </button>
          ))}
          {total > items.length && <div className="text-xs text-muted-foreground">Найдено: {total}</div>}
        </div>
      )}
    </AppearanceWrap>
  );
}

function FacetsBlock({ props, ctx }: { props: WidgetProps; ctx: WidgetContext }) {
  const { q, facet, setFacet } = useSiteSearchQuery();
  const [facets, setFacets] = useState<SearchFacet[]>([]);
  const [error, setError] = useState('');
  const fields = String(props.fields || '').split(',').map((s) => s.trim()).filter(Boolean);

  useEffect(() => {
    if (!q.trim() || !ctx.siteCode || fields.length === 0) { setFacets([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const resp = await getSDK().site.siteSearch({ site: ctx.siteCode || '', q, page: 0, pageSize: 1, facets: fields });
        if (!cancelled) setFacets((resp.facets || []) as SearchFacet[]);
      } catch (e) {
        if (!cancelled) setError(friendlyError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [q, ctx.siteCode, fields.join(',')]);

  if (fields.length === 0) return <ConfigHint text="Фасеты: укажите поля (через запятую) в свойствах блока" />;
  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      {error && <div className="text-sm text-destructive">{error}</div>}
      <div className="flex flex-wrap gap-2">
        {facet && (
          <button type="button" onClick={() => setFacet('')}
            className="rounded-full border border-[var(--site-border)] px-3 py-1 text-xs text-[var(--site-text)]">
            Все ✕
          </button>
        )}
        {facets.flatMap((f) => (f.values || []).map((v) => {
          const active = facet === `${f.field}:${v.value}`;
          return (
            <button key={`${f.field}:${v.value}`} type="button"
              onClick={() => setFacet(active ? '' : `${f.field}:${v.value}`)}
              className={`rounded-full px-3 py-1 text-xs border ${active
                ? 'bg-[var(--site-primary,var(--color-primary))] text-[var(--site-primary-foreground,#fff)] border-transparent'
                : 'border-[var(--site-border)] text-[var(--site-text)]'}`}>
              {v.label || v.value} · {v.count}
            </button>
          );
        }))}
      </div>
    </AppearanceWrap>
  );
}

// ----------------------------- регистрация -----------------------------

/** registerSiteMarketplaceBlocks — блоки Этапа 3 (вызывается из registerSiteBlocks). */
export function registerSiteMarketplaceBlocks(): void {
  registerWidget({
    type: 'form', display: 'Форма (заявка)', description: 'Гостевая форма → заявка (honeypot, rate-limit)', siteOk: true, appearanceSchema: true,
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text' },
      { key: 'fields', label: 'Поля (JSON [{name,label,type,required,placeholder,options}])', type: 'textarea', help: 'type: text|phone|email|textarea|select|date' },
      { key: 'submit', label: 'Сабмит (JSON {objectType,objectName,mapping,thanks})', type: 'textarea', help: 'mapping: {колонка заявки: имя поля}; thanks: {title,text,redirect}' },
      { key: 'submitLabel', label: 'Надпись кнопки', type: 'text' },
    ],
    render: (props, ctx) => <FormBlock props={props} ctx={ctx} />,
  });
  registerWidget({
    type: 'reviews', display: 'Отзывы', description: 'Список опубликованных отзывов объекта', siteOk: true, appearanceSchema: true,
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text' },
      { key: 'objectType', label: 'Тип объекта (catalog/document)', type: 'select', options: [
        { value: 'catalog', label: 'Справочник' }, { value: 'document', label: 'Документ' },
      ] },
      { key: 'objectCode', label: 'Код объекта', type: 'text' },
      { key: 'objectId', label: 'Запись (id; пусто = со страницы объекта)', type: 'text' },
      { key: 'limit', label: 'Сколько показать', type: 'number', placeholder: '10' },
    ],
    render: (props, ctx) => <ReviewsBlock props={props} ctx={ctx} />,
  });
  registerWidget({
    type: 'review_form', display: 'Форма отзыва', description: 'Оставить отзыв (оценка + текст; гости — с именем)', siteOk: true, appearanceSchema: true,
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text' },
      { key: 'objectType', label: 'Тип объекта (catalog/document)', type: 'select', options: [
        { value: 'catalog', label: 'Справочник' }, { value: 'document', label: 'Документ' },
      ] },
      { key: 'objectCode', label: 'Код объекта', type: 'text' },
      { key: 'objectId', label: 'Запись (id; пусто = со страницы объекта)', type: 'text' },
      { key: 'successText', label: 'Текст после отправки', type: 'text' },
    ],
    render: (props, ctx) => <ReviewFormBlock props={props} ctx={ctx} />,
  });
  registerWidget({
    type: 'rating', display: 'Рейтинг', description: 'Средняя оценка и количество отзывов', siteOk: true, appearanceSchema: true,
    propsSchema: [
      { key: 'objectType', label: 'Тип объекта (catalog/document)', type: 'select', options: [
        { value: 'catalog', label: 'Справочник' }, { value: 'document', label: 'Документ' },
      ] },
      { key: 'objectCode', label: 'Код объекта', type: 'text' },
      { key: 'objectId', label: 'Запись (id; пусто = со страницы объекта)', type: 'text' },
    ],
    render: (props, ctx) => <RatingBlock props={props} ctx={ctx} />,
  });
  registerWidget({
    type: 'search', display: 'Поиск по сайту', description: 'Поиск по целям сайта (settings.search.targets)', siteOk: true, appearanceSchema: true,
    propsSchema: [
      { key: 'placeholder', label: 'Подсказка поля', type: 'text' },
      { key: 'pageSize', label: 'Результатов на страницу', type: 'number', placeholder: '10' },
      { key: 'facets', label: 'Поля фасетов (через запятую)', type: 'text', help: 'значения фасетов приходят из блока «Фасеты» (состояние в URL)' },
    ],
    render: (props, ctx) => <SearchBlock props={props} ctx={ctx} />,
  });
  registerWidget({
    type: 'facets', display: 'Фасеты поиска', description: 'Счётчики-чипы по полям (фильтр через URL ?facet=)', siteOk: true, appearanceSchema: true,
    propsSchema: [
      { key: 'fields', label: 'Поля (через запятую)', type: 'text' },
    ],
    render: (props, ctx) => <FacetsBlock props={props} ctx={ctx} />,
  });
  // Контакты/карта (ТЗ §5): contacts + map (Leaflet лениво).
  registerSiteContactBlocks();
}
